import { useEffect, useRef, useState } from 'react';
import {
  ShoppingCart, DoorOpen, Wrench, ShieldCheck, PackageCheck, Truck, Building2, LifeBuoy,
} from 'lucide-react';

/**
 * The login page's picture of the business: every laptop travels one loop,
 * from vendor PO to the customer and back through support. Laptop tokens move
 * round the loop; a station pulses as a token reaches it and the centre logs
 * the step. Hover or tap a station to read what happens there.
 *
 * Positions are written straight to the DOM from one requestAnimationFrame
 * loop, so React re-renders only when the log or the hovered station changes.
 */

// Geometry in a 800 × 560 box; HTML is laid over it in percentages.
const W = 800;
const H = 560;
const CX = 400;
const CY = 280;
const RX = 318;
const RY = 205;
const LOOP_SECONDS = 56;
const TOKENS = 6;

export const STATIONS = [
  { icon: ShoppingCart, label: 'Purchase order', team: 'Procure', event: 'PO approved, code issued', text: 'A vendor PO is raised and approved. Each laptop gets its TTSPL code here and keeps it for life.' },
  { icon: DoorOpen, label: 'Gate inward', team: 'Guard', event: 'Scanned in at the gate', text: 'The guard scans every box in, and the GRN checks serial and configuration against the PO.' },
  { icon: Wrench, label: 'Brand new / refurbished', team: 'Floor', event: 'Prepared on the floor', text: 'Brand-new laptops are checked and set up; used ones are cleaned, repaired and upgraded — one ticket per laptop, timed and assigned.' },
  { icon: ShieldCheck, label: 'Quality check', team: 'QC', event: 'QC passed', text: 'QC tests every part and reads the live configuration before the laptop may enter stock.' },
  { icon: PackageCheck, label: 'Ready stock', team: 'Warehouse', event: 'Ready to rent', text: 'Tagged Ready-to-Rent or Ready-to-Sell and given a slot in the warehouse.' },
  { icon: Truck, label: 'Dispatch', team: 'Move', event: 'Left the gate', text: 'Challan, e-way bill and a gate scan out — by hand, porter or courier.' },
  { icon: Building2, label: 'With customer', team: 'Sell', event: 'Delivered with OTP', text: 'Handed over with OTP and photos. Rent, invoices and lock-in run from this moment.' },
  { icon: LifeBuoy, label: 'Support & return', team: 'Serve', event: 'Picked up for support', text: 'Tickets, repairs, replacements and returns — then back round the loop.' },
];

const N = STATIONS.length;
const STEP = (2 * Math.PI) / N;

function angleOf(index) {
  return -Math.PI / 2 + index * STEP;
}

function pointAt(angle) {
  return { x: CX + RX * Math.cos(angle), y: CY + RY * Math.sin(angle) };
}

function newCode() {
  return `TTSPL${String(1000 + Math.floor(Math.random() * 8999)).padStart(5, '0')}`;
}

const pct = (v, total) => `${(v / total) * 100}%`;

export default function LifecycleLoop() {
  const tokenRefs = useRef([]);
  const stationRefs = useRef([]);
  const [log, setLog] = useState([]);
  const [focus, setFocus] = useState(null);
  const codes = useRef(Array.from({ length: TOKENS }, newCode));

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const place = (i, phase) => {
      const el = tokenRefs.current[i];
      if (!el) return;
      const { x, y } = pointAt(-Math.PI / 2 + phase * 2 * Math.PI);
      el.style.left = pct(x, W);
      el.style.top = pct(y, H);
    };
    // Spread the tokens unevenly so the loop looks worked, not mechanical.
    const offsets = Array.from({ length: TOKENS }, (_, i) => i / TOKENS + 0.035 * Math.sin(i * 2.3));

    if (reduce) {
      offsets.forEach((o, i) => place(i, o + STEP / (4 * Math.PI)));
      return undefined;
    }

    const lastStation = offsets.map((o) => Math.floor(((o % 1) + 1) % 1 * N));
    let raf = 0;
    let seq = 0;
    const start = performance.now();
    const tick = (now) => {
      const elapsed = (now - start) / 1000;
      for (let i = 0; i < TOKENS; i += 1) {
        const phase = (((offsets[i] + elapsed / LOOP_SECONDS) % 1) + 1) % 1;
        place(i, phase);
        // Hide the code label while the laptop sits on a station, so it never covers the step name.
        const along = (phase * N) % 1;
        tokenRefs.current[i]?.classList.toggle('is-docked', Math.min(along, 1 - along) < 0.2);
        const station = Math.floor(phase * N);
        if (station !== lastStation[i]) {
          lastStation[i] = station;
          if (station === 0) {
            codes.current[i] = newCode();
            const label = tokenRefs.current[i]?.querySelector('[data-code]');
            if (label) label.textContent = codes.current[i];
          }
          const node = stationRefs.current[station];
          if (node) {
            node.classList.remove('is-hit');
            void node.offsetWidth; // restart the pulse animation
            node.classList.add('is-hit');
          }
          seq += 1;
          const entry = { id: seq, code: codes.current[i], station };
          setLog((prev) => [entry, ...prev].slice(0, 4));
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const focused = focus == null ? null : STATIONS[focus];
  const FocusIcon = focused?.icon;

  return (
    <div className="lp-loop" onMouseLeave={() => setFocus(null)}>
      <svg className="lp-loop__svg" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
        <defs>
          <linearGradient id="lpTrack" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#22D3EE" />
            <stop offset="55%" stopColor="#3B82F6" />
            <stop offset="100%" stopColor="#F59E0B" />
          </linearGradient>
        </defs>
        <ellipse cx={CX} cy={CY} rx={RX + 34} ry={RY + 34} className="lp-loop__halo" />
        <ellipse cx={CX} cy={CY} rx={RX} ry={RY} className="lp-loop__rail" />
        <ellipse cx={CX} cy={CY} rx={RX} ry={RY} className="lp-loop__flow" stroke="url(#lpTrack)" />
        {STATIONS.map((s, i) => {
          const a = angleOf(i);
          const inner = { x: CX + (RX - 46) * Math.cos(a), y: CY + (RY - 40) * Math.sin(a) };
          const p = pointAt(a);
          return <line key={s.label} x1={inner.x} y1={inner.y} x2={p.x} y2={p.y} className="lp-loop__spoke" />;
        })}
      </svg>

      {STATIONS.map((s, i) => {
        const a = angleOf(i);
        const p = pointAt(a);
        const Icon = s.icon;
        const side = Math.cos(a) > 0.25 ? 'right' : Math.cos(a) < -0.25 ? 'left' : Math.sin(a) < 0 ? 'top' : 'bottom';
        return (
          <button
            type="button"
            key={s.label}
            ref={(el) => { stationRefs.current[i] = el; }}
            className={`lp-station lp-station--${side}${focus === i ? ' is-focus' : ''}`}
            style={{ left: pct(p.x, W), top: pct(p.y, H) }}
            onMouseEnter={() => setFocus(i)}
            onFocus={() => setFocus(i)}
            onBlur={() => setFocus(null)}
            onClick={() => setFocus((f) => (f === i ? null : i))}
            aria-label={`${s.label}: ${s.text}`}
          >
            <span className="lp-station__dot"><Icon size={18} strokeWidth={2} /></span>
            <span className="lp-station__label">
              <span className="lp-station__step">{String(i + 1).padStart(2, '0')}</span>
              {s.label}
            </span>
          </button>
        );
      })}

      {Array.from({ length: TOKENS }, (_, i) => (
        <div key={i} className="lp-token" ref={(el) => { tokenRefs.current[i] = el; }} aria-hidden="true">
          <span className="lp-token__chip" />
          <span className="lp-token__code" data-code>{codes.current[i]}</span>
        </div>
      ))}

      <div className="lp-centre">
        {focused ? (
          <div className="lp-centre__focus" key={focus}>
            <span className="lp-centre__icon"><FocusIcon size={22} /></span>
            <p className="lp-centre__team">Step {focus + 1} · {focused.team}</p>
            <h3 className="lp-centre__title">{focused.label}</h3>
            <p className="lp-centre__text">{focused.text}</p>
          </div>
        ) : (
          <div className="lp-centre__idle">
            <p className="lp-eyebrow"><span className="lp-eyebrow__dot" />The Rentfoxxy loop</p>
            <h2 className="lp-centre__headline">Every laptop.<br />One code. <span>Every step.</span></h2>
            <ul className="lp-log">
              {log.map((e) => (
                <li key={e.id} className="lp-log__row">
                  <span className="lp-log__code">{e.code}</span>
                  <span className="lp-log__event">{STATIONS[e.station].event}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
